import React, { memo, useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Button, Spinner, Accordion } from "react-bootstrap";
import PositionWidget from "./PositionWidget";
import DateSelect from "../DateSelect";
import { getStillagePositions } from "services/stillageCollections";
import { toast } from "react-toastify";
import { grays } from "helpers/utils";
import dayjs from "dayjs";
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const PositionWidgetRow = () => {
    const [positionDates, setPositionDates] = useState({
        start: dayjs().startOf('isoWeek').toISOString(),
        end: dayjs().startOf('isoWeek').add(4, 'day').toISOString()
    });
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);

    const buildPositionType = useCallback((data, type) =>
        Object.keys(data).map(date => {
            const keys = Object.keys(data[date]).filter(key => key === type);
            const name = keys.join(',');
            return {
                date,
                [name]: {
                    estCount: keys.reduce((acc, key) => acc += data[date][key]?.estCount, 0),
                    actualCount: keys.reduce((acc, key) => acc += data[date][key]?.actualCount, 0),
                    totalFrames: keys.reduce((acc, key) => acc += data[date][key]?.totalFrames, 0),
                    plannedCSO: keys.reduce((acc, key) => acc += data[date][key]?.plannedCSO, 0),
                    plannedSF: keys.reduce((acc, key) => acc += data[date][key]?.plannedSF, 0),
                    actualCollected: keys.reduce((acc, key) => acc += data[date][key]?.actualCollected, 0),
                }
            }
        }), []);

    const getPositionData = useCallback(async (start, end) => {
        setLoading(true);
        getStillagePositions(start, end)
            .then(res => {
                const standard = buildPositionType(res?.counts, 'Standard');
                const aFrames = buildPositionType(res?.counts, 'stillage.aframe');
                const crane = buildPositionType(res?.counts, 'stillage.craneable');
                const specCrane = buildPositionType(res?.counts, 'stillage.special.craneable');
                setData({ standard, aFrames, crane, specCrane })
            })
            .catch((err) => {
                console.log(err);
                toast.error('Something went wrong! ', err);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => getPositionData(positionDates.start, positionDates.end), []);

    return (
        <Card className="w-100 p-3 mb-4">
            <Accordion defaultActiveKey="0" flush>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <span style={{ color: grays[600] }} className="text-decoration-none">Stillage Positions</span>
                    </Accordion.Header>
                    <Accordion.Body>
                        <DateSelect
                            title='Position Dates'
                            dates={positionDates}
                            setDates={setPositionDates}
                            clarifyingText='Position dates default to the current week'>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={() => getPositionData(positionDates.start, positionDates.end)}
                                disabled={loading}
                            >Search</Button>
                        </DateSelect>
                        <Row>
                            {
                                loading ?
                                    <Col xs={12} className="text-center"><Spinner variant="primary" /></Col> :
                                    <>
                                        <Col xs={6}>
                                            <PositionWidget title='Standard' data={data?.standard} typeKey='Standard' />
                                        </Col>
                                        <Col xs={6}>
                                            <PositionWidget title='A Frame' data={data?.aFrames} typeKey='stillage.aframe' />
                                        </Col>
                                        <Col xs={6}>
                                            <PositionWidget title='Std Craneable' data={data?.crane} typeKey='stillage.craneable' />
                                        </Col>
                                        <Col xs={6}>
                                            <PositionWidget title='Conquip' data={data?.specCrane} typeKey='stillage.special.craneable' />
                                        </Col>
                                    </>
                            }
                        </Row>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </Card>
    );
}

export default memo(PositionWidgetRow);